// components/notifications/NotificationModalWeb.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IoClose, IoNotificationsOutline } from 'react-icons/io5';
import cls from './NotificationModal.module.css';
import NotificationDetailModal from './NotificationDetailModal';
import { useTranslation } from 'react-i18next';
/* ------------------------------------------------------------------ */
/*  Animated green dot                                                */
/* ------------------------------------------------------------------ */
const Dot = () => (
    <span className={cls.dot} />
);

/* ------------------------------------------------------------------ */
/*  Modal                                                             */
/* ------------------------------------------------------------------ */
const NotificationModal = ({
    visible,
    onClose,
    notifications = [],
    loading,
    markLocalRead,
    getUserState,

}) => {
    const { userId, userType } = getUserState();
    const [selected, setSelected] = useState(null);
    const [tab, setTab] = useState('unread'); // 'unread' | 'read'
    const { i18n, t } = useTranslation();
    const dir = i18n.dir();
    /* reset when closed ------------------------------------------------ */
    useEffect(() => {
        if (!visible) {
            setSelected(null);
            setTab('unread');
        }
    }, [visible]);

    /* filtering -------------------------------------------------------- */
    const list = notifications.filter((n) =>
        tab === 'unread' ? !n.isRead : n.isRead
    );

    /* ------------------------------------------------------------------ */
    /*  list item                                                         */
    /* ------------------------------------------------------------------ */
    const renderItem = useCallback(
        (n) => (
            <button
                key={n.id}
                className={cls.card}
                onClick={() => setSelected(n)}
            >
                <div className={cls.cardHeader}>
                    <h4 className={cls.cardTitle}>{n.title}</h4>
                    {!n.isRead && <Dot />}
                </div>

                {!!n.imageUrl && (
                    <div className={cls.thumb}>
                        <img src={n.imageUrl} alt="" />
                    </div>
                )}

                <p className={cls.cardBody}>{n.body}</p>
                <span className={cls.cardDate}>
                    {new Date(n.startAt).toLocaleString()}
                </span>
            </button>
        ),
        []
    );

    /* ------------------------------------------------------------------ */
    /*  render                                                            */
    /* ------------------------------------------------------------------ */
    if (!visible) return null;

    return createPortal(
        <>
            {/* backdrop */}
            <div className={cls.backdrop} onClick={onClose} />

            {/* sheet */}
            <section className={cls.modal} role="dialog" aria-modal="true" dir={dir}>
                {/* header */}
                <header className={cls.header}>
                    <h3>{t('Notifications')}</h3>
                    <button onClick={onClose} className={cls.close}>
                        <IoClose size={26} />
                    </button>
                </header>

                {/* tabs */}
                <nav className={cls.tabs}>
                    {['unread', 'read'].map((k) => (
                        <button
                            key={k}
                            className={`${cls.tab} ${tab === k ? cls.tabActive : ''}`}
                            onClick={() => setTab(k)}
                        >
                            {t(k === 'unread' ? 'Unread' : 'Read')}
                        </button>
                    ))}
                </nav>

                {/* body */}
                <div className={cls.content}>
                    {loading ? (
                        <div className={cls.loader} />
                    ) : list.length === 0 ? (
                        <div className={cls.empty}>{t('NoNotifications')}</div>
                    ) : (
                        <div className={cls.scrollArea}>{list.map(renderItem)}</div>
                    )}
                </div>
            </section>

            {/* nested detail modal */}
            {selected && (
                <NotificationDetailModal
                    notification={selected}
                    onClose={() => setSelected(null)}
                    markLocalRead={markLocalRead}
                    userId={userId}
                    userType={userType}
                    t={t}
                    dir={dir}
                />
            )}
        </>,
        document.body
    );
};

export default NotificationModal;
